import PropTypes from 'prop-types';
import { useMemo } from 'react';
import MillesimaLink from '@/components/common/MillesimaLink';
import { PAGES } from '@/constants/pages';
import { LinkPropTypes } from '@/constants/propTypes';
import { buildMenuLink, normalizeUrl } from '@/utils/menuUtils';
import styles from './DesktopMenuLink.module.scss';

const customPages = {
	...PAGES,
	EC: '/exclusive-cellar.html',
};

const DesktopMenuLink = ({
	id,
	link,
	type,
	linkClassName,
	handleMouseEnter,
	handleMouseLeave,
	handleClick,
	children,
	fontColor,
	isOpen,
	isActiveLink,
}) => {
	let href;
	const {
		isNofollow,
		picto: icon,
		alt = 'icon',
		urlKeyword: url,
		name: text,
		subtext,
		img: image,
		showText = true,
		pictoComponent,
	} = link;

	const getLinkColor = () => {
		if (linkClassName) {
			const arrLinkClassName = linkClassName.split(' ');
			return arrLinkClassName.indexOf('promo') !== -1 ? 'promo' : '';
		}
		return '';
	};

	const alignItemsCenter = pictoComponent || icon ? 'mx-0 d-flex align-items-center h-100' : '';

	if (Object.values(customPages).includes(url)) {
		href = {
			pathname: url,
			query: {
				type: 'defaultType', // To create a dynamic link, otherwise it just uses an <a> tag
			},
		};
	} else {
		href = url ? buildMenuLink(link) : '#';
	}

	const iconClassName = [styles['icon-container'], !showText && styles['icon-container-without-text']].filter(Boolean).join(' ');

	const linkElement = useMemo(
		() => (
			<MillesimaLink
				id={id}
				customClassName={{ link: `w-100 ${linkClassName ? linkClassName : ''}` }}
				ariaLabel={text}
				isNofollow={isNofollow || !url}
				href={href}
				as={url ? normalizeUrl(url) : null}
				isWithoutContainer={!isNofollow}
			>
				{image && <img className={`d-flex mx-auto ${styles['image']}`} src={image} alt="image" loading="lazy" />}
				<div className={[image && 'text-center', alignItemsCenter].filter(Boolean).join(' ') || null}>
					{(icon || pictoComponent) && (
						<div className={iconClassName} data-test-id="icon">
							{icon && <img src={icon} alt={alt} className={styles['icon']} width={24} height={24} loading="lazy" />}
							{pictoComponent}
						</div>
					)}
					{showText && (
						<>
							{subtext ? (
								<div>
									<div style={{ color: fontColor }} className={`${styles['link-text']}`} data-test-id="link">
										{text}
									</div>
									<div style={{ color: fontColor }} className={styles['link-subtext']}>
										{subtext}
									</div>
								</div>
							) : (
								<div style={{ color: fontColor }} className={`${styles['link-text']}`} data-test-id="link">
									{text}
								</div>
							)}
						</>
					)}
				</div>
			</MillesimaLink>
		),
		[id, linkClassName, text, subtext, image, icon, alt, type, showText, pictoComponent, isNofollow],
	);

	return (
		<div
			tabIndex="0"
			className={`${styles['desktop-menu-link']} ${type ? styles[type.toLowerCase()] : ''} ${isOpen ? styles['open'] : ''} ${styles[getLinkColor()]} ${isActiveLink ? styles['show'] : ''}`}
			data-test-id="desktop-menu-link"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={() => handleClick?.()}
			onFocus={handleMouseEnter}
			onBlur={handleMouseLeave}
		>
			{linkElement}
			{children}
		</div>
	);
};

DesktopMenuLink.propTypes = {
	id: PropTypes.string,
	link: PropTypes.shape(LinkPropTypes).isRequired,
	type: PropTypes.string,
	linkClassName: PropTypes.string,
	handleMouseEnter: PropTypes.func,
	handleMouseLeave: PropTypes.func,
	handleClick: PropTypes.func,
	children: PropTypes.object,
	fontColor: PropTypes.string,
	isOpen: PropTypes.bool,
	isActiveLink: PropTypes.bool,
};

export default DesktopMenuLink;
